import { dimensionValues, labelOf } from './dimensions';
import { postedAt, postedLabel } from './posted';
import { HASHTAG_LABELS, KEYWORD_LABELS, type MentionRow } from './generator';
import { REFERENCE_DATE } from './reference';
import { allRows } from './rows';
import { rowId, type RowId } from './sentimentOverrides';

/**
 * Quick search — page 2048:10031, four frames.
 *
 * The module is a query, not a dashboard: one channel, a cap on how many
 * results to return, and up to ten search terms. It draws no `Filters Bar` (see
 * `NO_FILTERS_BAR` in `router/routes.ts`), so **nothing here reads the dashboard
 * store** — not the chips, not the date range. A quick search is scoped by what
 * was typed into it and by nothing else, which is the same call DeepDive AI's
 * answers make for the same reason: the question is the scope.
 *
 * The corpus is the app's own `allRows` — every brand, all time. That is the
 * honest model of what the screen claims to do. It searches the listening feed,
 * and the feed does not stop at our brand or at the period some chart elsewhere
 * happens to be showing.
 */

/** Figma `Max results *`, drawn filled with `100`. */
export const DEFAULT_MAX_RESULTS = 100;

/**
 * Figma's hint — "You can enter up to 10 entries" — and the blurb above the tabs
 * says the same thing in words. Enforced rather than merely stated: the tag
 * field stops accepting an eleventh term.
 */
export const MAX_TERMS = 10;

/** Figma draws ten body rows under a five-page pager. */
export const RESULTS_PAGE_SIZE = 10;

/**
 * The channel picker's options, read out of the `Channels` chip fixture rather
 * than retyped — the rule `dimensions.ts` keeps for every other value list.
 * Figma draws the field holding `Facebook` with a `logos:facebook` mark in the
 * leading slot, and Facebook is `dimensionValues.channel[0]`.
 */
export const quickSearchChannels: readonly string[] = dimensionValues.channel;

export const DEFAULT_CHANNEL = quickSearchChannels[0];

/** One row of the `Search results` table. */
export type QuickSearchResult = {
  id: RowId;
  /**
   * The corpus row behind this result, carried through so `View` can open the
   * `Post breakdown` drawer on it — the one place in this app that shows a
   * single post. A result without it would have to look the row up again by id,
   * which `allRows` offers no index for.
   */
  row: MentionRow;
  /** Figma `Conversation` — the post, in one line. */
  conversation: string;
  reactions: number;
  comments: number;
  shares: number;
  /** Figma `Date posted` — when the post was published. */
  postedAt: number;
};

/** One row of the `Search history` table. */
export type QuickSearchHistoryEntry = {
  id: string;
  /** Figma `Query` — the terms that were searched, as typed. */
  query: string;
  /**
   * The same terms, unjoined — what `Details` re-runs to fill the query screen
   * (2206:80793).
   *
   * Kept beside `query` rather than split back out of it: `query` is a display
   * string built with `', '`, and a term containing a comma would split into
   * two searches that were never run. The list is the fact; the string is how
   * the cell shows it.
   */
  terms: readonly string[];
  /** Figma `Channel` — drawn as the platform's own full-colour mark. */
  channel: string;
  /**
   * The TikTok query's own three, unset for every other channel — so `Details`
   * re-runs a TikTok search the way it was run, not as a plain keyword search
   * over the whole corpus. See `QuickSearchQuery`, whose fields these are.
   */
  mode?: QuickSearchMode;
  days?: number;
  sort?: QuickSearchSort;
  /** Figma `Date range` — the window the search covered. */
  range: string;
  /** Figma `Date posted` — see `HISTORY_DATE_HEADER`. */
  ranAt: number;
};

const whole = new Intl.NumberFormat('en-US');

export const formatMetric = (value: number): string => whole.format(value);

/**
 * `Mon Sep 14, 9:42 PM`. Figma fills every date cell with `12,975` — the table
 * component's own placeholder number, copied down every column — so the format
 * is a decision rather than a reading of the design, and it is made once for
 * every table in `data/posted.ts`.
 */
export const dateLabel = postedLabel;

/** The word a row matched, in the script it is written in. `network` → `নেটওয়ার্ক`. */
const keywordLabel = (row: MentionRow): string => KEYWORD_LABELS[row.keyword] ?? row.keyword;

/**
 * What one mention reads as in the `Conversation` column.
 *
 * The corpus carries no post text — it is structured mentions, not scraped
 * copy — so the line is composed from the row's own fields, which is the same
 * gap and the same answer as `postSummary` in `posts.ts`. Two differences, both
 * because of where this table sits:
 *
 * - **No channel.** The query already fixed it, and every row in the table
 *   shares it, so a column repeating it would carry no information.
 * - **The matched word, last.** The user typed a term and the first question
 *   about any row is why it came back. `postSummary` has no such question to
 *   answer and does not carry one.
 *
 * Figma fills every cell with `Olivia Rhye`, which is the table component's
 * example content and names nothing about this module.
 */
export function conversationSummary(row: MentionRow): string {
  const brand = labelOf('brand', row.brand);
  const topic = labelOf('topic', row.topic);
  const intent = labelOf('intent', row.intent).toLowerCase();
  return `${brand} · ${topic} ${intent} · “${keywordLabel(row)}”`;
}

/**
 * Every string one row can be found by.
 *
 * Both the slug and the native label for the keyword and the hashtag, so
 * `network` and `নেটওয়ার্ক` find the same rows — the corpus files the word as
 * `network` and prints it as `নেটওয়ার্ক`, and a user searching this screen may
 * reasonably type either. The composed summary goes in too, which is what makes
 * a brand, a topic or an intent searchable without each needing its own branch.
 */
function haystack(row: MentionRow): string {
  const tag = row.hashtag === null ? '' : `${row.hashtag} ${HASHTAG_LABELS[row.hashtag] ?? ''}`;
  return `${row.keyword} ${keywordLabel(row)} ${tag} ${conversationSummary(row)}`.toLowerCase();
}

/**
 * A row's hashtag alone, for `mode: 'hashtag'` — both the stored value and the
 * label it is written with, so `#mygp` and `mygp` both find it.
 *
 * Narrower than `haystack` on purpose: searching *by hashtag* has to mean the
 * hashtag, or the mode is a label over the same search. A row carrying no
 * hashtag can never match one, which is why this returns an empty string rather
 * than falling back to the post.
 */
function hashtagHaystack(row: MentionRow): string {
  if (row.hashtag === null) return '';
  return `${row.hashtag} ${HASHTAG_LABELS[row.hashtag] ?? ''}`.toLowerCase();
}

/** TikTok's `How do you want to search?` — 2208:89736 and 2208:90048. */
export type QuickSearchMode = 'keyword' | 'hashtag';

/**
 * TikTok's `Sort by` (2208:89736). The four the corpus can actually order on:
 * every row carries likes, comments, shares and a posting time.
 */
export type QuickSearchSort = 'liked' | 'commented' | 'shared' | 'recent';

export const QUICK_SEARCH_SORTS: readonly { value: QuickSearchSort; label: string }[] = [
  { value: 'liked', label: 'Most liked' },
  { value: 'commented', label: 'Most commented' },
  { value: 'shared', label: 'Most shared' },
  { value: 'recent', label: 'Most recent' },
];

/**
 * TikTok's `Time period` (2208:89736), in days — the ranges `Search history`
 * already records, so a TikTok search and the row it writes read the same.
 */
export const QUICK_SEARCH_PERIODS: readonly { value: string; label: string; days: number }[] = [
  { value: '1', label: 'Last 24 hours', days: 1 },
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
];

/**
 * TikTok's `Country` (2208:89736).
 *
 * **Nothing filters on it.** The corpus is one country's conversation — it
 * models six Bangladesh divisions and carries no country field at all — so this
 * records the scope a TikTok search was run at and cannot narrow the rows.
 * Drawn, required and written to history; inert against the data. The four
 * values are the user's (2026-09-23); flagged for the designer, because a
 * required control that cannot change a result is the one thing on this form
 * that does not do what it looks like it does.
 */
export const QUICK_SEARCH_COUNTRIES: readonly string[] = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
];

export type QuickSearchQuery = {
  channel: string;
  terms: readonly string[];
  maxResults: number;
  /**
   * What the terms are matched against. `keyword` searches the post the way
   * every other channel does; `hashtag` searches the hashtag only. TikTok's
   * two frames; every other channel leaves it unset, which reads as `keyword`.
   */
  mode?: QuickSearchMode;
  /**
   * Cut the results to the last N days, counted back from `REFERENCE_DATE` —
   * the prototype's today, not the clock's, so two runs a week apart return the
   * same rows. TikTok's `Time period` and `Timeframe`; unset means the whole
   * corpus, which is what every other channel searches.
   */
  days?: number;
  /** TikTok's `Sort by`. Unset is `recent`, the order the other channels use. */
  sort?: QuickSearchSort;
};

/**
 * The rows a query returns: this channel, matching **any** of the terms, newest
 * first, capped at `maxResults`.
 *
 * `any` rather than `all` is what the field's own affordance implies — ten
 * separate chips, each its own term, is a list of things to look for, not a
 * conjunction that would empty the table the moment a second word was added.
 *
 * Newest first because `Date posted` is the one order the user has not asked
 * for; the other columns sort on click.
 *
 * The blurb promises "keyword, hashtag, page or profile URL, or a specific post
 * link". This corpus models the first two and holds no URLs at all, so a term
 * that looks like a link simply finds nothing — the truthful answer rather than
 * a fabricated hit. Flagged in plan.md §7.
 */
export function quickSearchResults({
  channel,
  terms,
  maxResults,
  mode = 'keyword',
  days,
  sort = 'recent',
}: QuickSearchQuery): QuickSearchResult[] {
  const wanted = terms
    /* `#mygp` and `mygp` are the same search — the hash is how a tag is written, not part of it. */
    .map((term) => term.trim().toLowerCase().replace(/^#/, ''))
    .filter((term) => term !== '');
  if (wanted.length === 0) return [];

  /*
   * Counted back from the corpus' own today. `Date.now()` would walk the window
   * every day the prototype is opened and eventually empty it.
   */
  const since = days === undefined ? undefined : REFERENCE_DATE.getTime() - days * DAY;

  const hits: QuickSearchResult[] = [];
  for (const row of allRows) {
    if (row.channel !== channel) continue;
    if (since !== undefined && postedAt(row) < since) continue;
    const text = mode === 'hashtag' ? hashtagHaystack(row) : haystack(row);
    if (text === '') continue;
    if (!wanted.some((term) => text.includes(term))) continue;
    hits.push({
      id: rowId(row),
      row,
      conversation: conversationSummary(row),
      /*
       * Figma's column is `Reactions`, singular concept. The corpus splits
       * interactions four ways and `likes` is a reaction — folding the two is
       * what keeps this column comparable with `Engagement by type`, where the
       * same pair sits under the same heading.
       */
      reactions: row.reactions + row.likes,
      comments: row.comments,
      shares: row.shares,
      /*
       * `day` is local midnight and `hour` carries the time of day — see the
       * note on `MentionRow.hour`. Recombining them here is what lets two posts
       * from the same day order against each other.
       */
      postedAt: postedAt(row),
    });
  }

  /*
   * `Sort by`, newest first by default — the order every non-TikTok search
   * returns and the one `Date posted` is not asked for on. Ties fall to the
   * newer post, so a sort by likes is still stable between renders.
   */
  const by: Record<QuickSearchSort, (hit: QuickSearchResult) => number> = {
    liked: (hit) => hit.reactions,
    commented: (hit) => hit.comments,
    shared: (hit) => hit.shares,
    recent: (hit) => hit.postedAt,
  };
  hits.sort((a, b) => by[sort](b) - by[sort](a) || b.postedAt - a.postedAt);
  /*
   * `Math.max(1, …)` so a cap typed as `0`, or cleared, does not silently mean
   * "every result". The field is `Max results *` — required — and the smallest
   * honest answer to it is one.
   */
  return hits.slice(0, Math.max(1, Math.floor(maxResults)));
}

const DAY = 86_400_000;

/**
 * The ten rows Figma draws in `Search history`, and the `10` its badge counts.
 *
 * Seeded rather than recorded: the frame shows a populated history on a screen
 * the user has only just opened, so the list cannot be only what this session
 * did. Searches run in the session are prepended to it — see `QuickSearchView`.
 *
 * Terms are drawn from the corpus' own vocabulary, so every row here, re-run,
 * finds something. Dates walk back a day at a time from the prototype's
 * `REFERENCE_DATE` rather than from the clock, which is what keeps two reloads
 * identical.
 */
export const seedHistory: readonly QuickSearchHistoryEntry[] = (
  [
    { terms: ['mygp', 'dhaka'], channel: 'facebook', range: 'Last 30 days' },
    { terms: ['network'], channel: 'facebook', range: 'Last 7 days' },
    { terms: ['recharge', 'bkash'], channel: 'instagram', range: 'Last 30 days' },
    { terms: ['roaming'], channel: 'x', range: 'Last 24 hours' },
    { terms: ['banglalink', 'offer'], channel: 'youtube', range: 'Last 90 days' },
    { terms: ['robi', 'bill'], channel: 'facebook', range: 'Last 7 days' },
    { terms: ['internet', 'speed'], channel: 'tiktok', range: 'Last 30 days' },
    { terms: ['teletalk'], channel: 'linkedin', range: 'Last 90 days' },
    { terms: ['airtel', 'signal'], channel: 'reddit', range: 'Last 30 days' },
    { terms: ['five-g'], channel: 'facebook', range: 'Last 24 hours' },
  ] as const
).map((entry, index) => ({
  id: `seed-${index}`,
  query: entry.terms.join(', '),
  terms: [...entry.terms],
  channel: entry.channel,
  range: entry.range,
  ranAt: REFERENCE_DATE.getTime() - index * DAY,
}));

/**
 * Figma's second history column reads `Date posted`, which is the results
 * table's header copied across — a history row records when a *search* ran, and
 * nothing in it was posted. The label is kept exactly as drawn and the column is
 * fed the time the search ran. Flagged for the designer in plan.md §7.
 */
/**
 * A seeded history entry's query, for the breadcrumb on `#quick-search/<id>`.
 *
 * Seeds only, the same limit `seedChatTitle` has and for the same reason: the
 * router runs outside React and cannot read the module's live history, so a
 * search run this session crumbs by its id. `HeaderView` overlays the live name
 * for chats and reports, which it can do because both live in a store; quick
 * search's history is `QuickSearchView`'s own state, so there is nothing for it
 * to read. The screen itself resolves every entry — only the crumb is seeded.
 */
export function seedHistoryQuery(id: string): string | undefined {
  return seedHistory.find((entry) => entry.id === id)?.query;
}

export const HISTORY_DATE_HEADER = 'Date posted';

/**
 * The window a search covered, for a search run in this session.
 *
 * Quick search draws no date picker and no `Filters Bar`, so a search made here
 * has no window to record — it reads the whole corpus. `All time` is what that
 * is, written the way the `Date range` column writes everything else.
 */
export const SESSION_RANGE = 'All time';
